import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, ResolveFn } from '@angular/router';
import { Observable, forkJoin, of } from 'rxjs';
import { catchError, concatMap, switchMap } from 'rxjs/operators';
import {
  FirstBatchResult,
  SecondBatchResult,
  enrichScenarioWithFirstBatch,
  enrichScenarioWithSecondBatch,
  createConditionalLoadProfileObservables,
  createCostTimeseriesObservable,
  createCostTariffChartsObservable,
  handleResolverError,
} from '@helper';
import {
  ScenarioPrograms,
  ScenarioResolverResult,
  CostTimeseries,
  TariffCharts,
} from '@model';
import {
  CookieService,
  EssService,
  ForecastService,
  GlobalAlertService,
  GridProgramService,
  LoadProfileService,
  LoaderService,
  ProjectService,
  ResultService,
  ScenarioService,
  SiteConstraintService,
  SolarPvService,
  TariffService,
} from '@service';

/**
 * Scenario resolver function (Angular 14+ functional API)
 * Replaces the deprecated class-based ScenarioResolver
 * 
 * This resolver handles both:
 * - Creating a new scenario (when no scenarioId is provided)
 * - Loading an existing scenario with all its dependencies
 * 
 * @returns Observable of tuple for backward compatibility with existing components
 */
export const scenarioResolver: ResolveFn<ScenarioResolverResult> = (
  route: ActivatedRouteSnapshot
): Observable<ScenarioResolverResult> => {
  // Inject all required services
  const scenarioService = inject(ScenarioService);
  const cookieService = inject(CookieService);
  const essService = inject(EssService);
  const projectService = inject(ProjectService);
  const loadProfileService = inject(LoadProfileService);
  const forecastService = inject(ForecastService);
  const solarPvService = inject(SolarPvService);
  const resultService = inject(ResultService);
  const tariffService = inject(TariffService);
  const siteConstraintService = inject(SiteConstraintService);
  const globalAlertService = inject(GlobalAlertService);
  const loaderService = inject(LoaderService);
  const gridProgramService = inject(GridProgramService);

  // Extract route parameters
  const projectId =
    route.queryParamMap.get('projectId') || route.paramMap.get('projectId');
  const scenarioId =
    route.queryParamMap.get('scenarioId') || route.paramMap.get('scenarioId');
  const locale = cookieService.getLocale();

  // Common observables for both paths (new and existing scenario)
  const project$ = projectService.getProject$(projectId);
  const loadProfiles$ = loadProfileService.loadProfiles$(projectId);
  const pvLoadProfiles$ = solarPvService.pvLoadProfiles$(projectId);
  const bess$ = essService.bess$();
  const minLoadInputs$ = siteConstraintService.getDynamicInput$('site-constraint');
  const maxLoadInputs$ = siteConstraintService.getDynamicInput$('max-load-site-constraint');
  const storedRates$ = tariffService.getStoredRates$();
  const energyForecasts$ = forecastService.getEnergyForecasts(projectId);
  const solarForecasts$ = forecastService.getSolarForecasts(projectId);

  // ============================================
  // PATH 1: Loading existing scenario
  // ============================================
  if (scenarioId) {
    loaderService.changeLabel('loader.message.loading_summary');

    // Parameters that depend on scenarioId
    const gridParameters$ = gridProgramService.gridParameters$(scenarioId);
    const essParameters$ = essService.getParameters$(scenarioId);
    const tariffParameters$ = tariffService.getParameters$(scenarioId);
    const minSiteParameters$ = siteConstraintService.getSiteConstraint$(scenarioId);
    const maxSiteParameters$ = siteConstraintService.getSiteConstraint$(scenarioId, true);

    // Scenario-specific data
    const existingScenario$ = scenarioService.getScenario$(scenarioId);
    const result$ = resultService.getResults$(scenarioId);
    const tariff$ = tariffService.getTariffs$(scenarioId);
    const ders$ = essService.getDers$(scenarioId);
    const selectedPrograms$ = gridProgramService.getSelectedPrograms$(scenarioId);

    // First batch: Load scenario with its core data
    // Each observable has its own error handler to prevent cascading failures
    const firstResult$ = forkJoin([
      existingScenario$.pipe(catchError(() => of(null))),
      result$.pipe(catchError(() => of(null))),
      tariff$.pipe(catchError(() => of([]))),
      ders$.pipe(catchError(() => of([]))),
      essParameters$.pipe(catchError(() => of(null))),
      tariffParameters$.pipe(catchError(() => of(null))),
      minSiteParameters$.pipe(catchError(() => of(null))),
      maxSiteParameters$.pipe(catchError(() => of(null))),
      gridParameters$.pipe(catchError(() => of(null))),
      selectedPrograms$.pipe(catchError(() => of([]))),
    ]);

    // Second batch: Load profiles and additional data based on scenario properties
    const secondResult$ = firstResult$.pipe(
      concatMap((firstBatch: FirstBatchResult) => {
        const scenario = enrichScenarioWithFirstBatch(firstBatch[0], firstBatch);

        // Create conditional load profile observables using helper
        const conditionalObservables = createConditionalLoadProfileObservables(
          scenario,
          loadProfileService,
          solarPvService,
          forecastService,
        );

        // Each observable with error protection for resilience
        return forkJoin([
          of(scenario),
          projectService.getProject$(projectId).pipe(catchError(() => of(null))),
          conditionalObservables.loadProfile$.pipe(catchError(() => of(null))),
          conditionalObservables.loadProfileJson$.pipe(catchError(() => of(null))),
          conditionalObservables.pvLoadProfile$.pipe(catchError(() => of(null))),
          scenario.loadprofileId && scenario.pvloadId
            ? conditionalObservables.adjustedLoadProfile$.pipe(catchError(() => of(null)))
            : of(null),
          scenario.loadprofileId && scenario.pvloadId
            ? conditionalObservables.adjustedJson$.pipe(catchError(() => of(null)))
            : of(null),
          scenario.loadprofileId
            ? conditionalObservables.forecastedLoadProfile$.pipe(catchError(() => of(null)))
            : of(null),
          scenario.pvloadId
            ? conditionalObservables.forecastedPvLoadProfile$.pipe(catchError(() => of(null)))
            : of(null),
        ]);
      }),
      catchError((err: unknown) => handleResolverError(err, globalAlertService)),
    );

    // Third batch: Assemble final result with all data
    const thirdResult$ = secondResult$.pipe(
      concatMap((secondBatch: SecondBatchResult) => {
        const scenario = enrichScenarioWithSecondBatch(secondBatch[0], secondBatch);
        const project = secondBatch[1];

        // Create cost timeseries observable using helper
        const costTimeseries$ = createCostTimeseriesObservable(
          scenarioService,
          scenarioId,
          scenario,
        );

        // Create cost tariff charts observable using helper
        const costTariffCharts$ = createCostTariffChartsObservable(
          scenarioService,
          scenarioId,
          scenario,
        );

        // Each observable with error protection for resilience
        return forkJoin([
          of(scenario),
          of(project),
          loadProfiles$.pipe(catchError(() => of([]))),
          bess$.pipe(catchError(() => of([]))),
          pvLoadProfiles$.pipe(catchError(() => of([]))),
          minLoadInputs$.pipe(catchError(() => of([]))),
          maxLoadInputs$.pipe(catchError(() => of([]))),
          storedRates$.pipe(catchError(() => of([]))),
          energyForecasts$.pipe(catchError(() => of([]))),
          solarForecasts$.pipe(catchError(() => of([]))),
          costTimeseries$.pipe(catchError(() => of([]))),
          costTariffCharts$.pipe(catchError(() => of([]))),
        ]);
      }),
      catchError((err: unknown) => handleResolverError(err, globalAlertService)),
    );

    return thirdResult$;
  }

  // ============================================
  // PATH 2: Creating new scenario
  // ============================================
  loaderService.changeLabel('');

  // Create new scenario
  const createScenario$ = scenarioService.createScenario$({
    displayLabel: '',
    parentId: projectId,
    locale,
  });

  // Update scenario with default display label
  const newScenario$ = createScenario$.pipe(
    switchMap((scenario) => {
      scenario.displayLabel = `Untitled Scenario ${scenario.id}`;
      return scenarioService.updateScenario$(scenario.id, scenario);
    }),
  );

  // Finalize new scenario with empty programs
  const updateScenario$ = newScenario$.pipe(
    concatMap((scenario) => {
      scenario.programs = new ScenarioPrograms();
      return of(scenario);
    }),
  );

  // Return new scenario with all required supporting data
  // Each observable with error protection for resilience
  return forkJoin([
    updateScenario$,
    project$.pipe(catchError(() => of(null))),
    loadProfiles$.pipe(catchError(() => of([]))),
    bess$.pipe(catchError(() => of([]))),
    pvLoadProfiles$.pipe(catchError(() => of([]))),
    minLoadInputs$.pipe(catchError(() => of([]))),
    maxLoadInputs$.pipe(catchError(() => of([]))),
    storedRates$.pipe(catchError(() => of([]))),
    energyForecasts$.pipe(catchError(() => of([]))),
    solarForecasts$.pipe(catchError(() => of([]))),
    of([] as CostTimeseries[]),
    of([] as TariffCharts[]),
  ]);
};
